/**
 * RentalExpenses — Accounts sub-page for property rentals (charging
 * points + staff accommodation). Full CRUD with cheque copy uploads.
 *
 * Backed by:
 *   GET/POST /api/accounts/rental-expenses
 *   GET/PUT/DELETE /api/accounts/rental-expenses/{id}
 *   POST /api/accounts/rental-expenses/{id}/payments
 *   DELETE /api/accounts/rental-expenses/{id}/payments/{pid}
 *   POST /api/accounts/rental-expenses/{id}/upload  (multipart)
 */
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from './ui/table';
import {
  Plus, Trash2, Loader2, FileText, Building, Zap,
  Home, Upload, Eye, CalendarDays, X,
} from 'lucide-react';
import { formatErrorMessage } from '../utils/formatError';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const formatMoney = (n) => new Intl.NumberFormat('en-AE', {
  style: 'currency', currency: 'AED', minimumFractionDigits: 2,
}).format(Number(n || 0));

const emptyForm = {
  property_type: 'charging_point',
  property_name: '',
  swap_location_id: '',
  real_estator: '',
  real_estator_contact: { name: '', email: '', phone: '' },
  property_owner: '',
  tenancy_contract_no: '',
  dewa_premises_no: '',
  tenancy_start_date: '',
  tenancy_expiry_date: '',
  tenancy_contract_urls: [],
  contract_value: 0,
  security_deposit: 0,
  dewa_deposit: 0,
  // Analysis dashboard overrides — leave null/empty to inherit global
  // slider defaults from the Analysis "Shared-premises allocation" card.
  shared_pct_electricity: '',
  shared_pct_rent: '',
  other_deposits: [],
  other_fees: [],
  // Payments captured at property-creation time. Each entry has the
  // same shape as the POST /payments body. Optional — user can also
  // add payments later from the Detail view.
  initial_payments: [],
};

const emptyChequePayment = () => ({
  _key: Math.random().toString(36).slice(2),
  category: 'contract_value',
  mode: 'cheque',
  cheques: [{ cheque_no: '', cheque_type: 'PDC', date: '', amount: 0, copy_url: '' }],
  bank_transfer: null,
});

const CATEGORY_OPTIONS = [
  { value: 'contract_value',   label: 'Contract Value' },
  { value: 'security_deposit', label: 'Security Deposit' },
  { value: 'dewa_deposit',     label: 'DEWA Deposit' },
  { value: 'other_deposits',   label: 'Other Deposits' },
  { value: 'other_fees',       label: 'Other Fees' },
];

const CATEGORY_LABEL = Object.fromEntries(CATEGORY_OPTIONS.map(o => [o.value, o.label]));


// PDC Cheque reminder recipients + audit log panel — sits above the
// property table so ops can manage without touching F12.
const PdcReminderPanel = () => {
  const [cfg, setCfg] = useState(null);
  const [emailInput, setEmailInput] = useState('');
  const [waInput, setWaInput] = useState('');
  const [logRows, setLogRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const authHeader = () => ({ Authorization: 'Bearer ' + localStorage.getItem('token') });

  // Format the WA recipients list (of {name, phone} dicts OR legacy phone
  // strings) into a textarea-friendly "Name | Phone" per-line block.
  const formatWaRecipients = (arr) => (arr || []).map((r) => {
    if (typeof r === 'string') return r;
    if (r && typeof r === 'object') {
      const nm = (r.name || '').trim();
      const ph = (r.phone || r.number || '').trim();
      return nm ? `${nm} | ${ph}` : ph;
    }
    return '';
  }).filter(Boolean).join('\n');

  // Parse the textarea back to a [{name, phone}] array. Accepts:
  //   • one entry per line (preferred): `Name | +9715...`
  //   • or a comma-separated list of bare phones (legacy)
  const parseWaRecipients = (raw) => {
    const parts = (raw || '').split(/[,\n]/).map(s => s.trim()).filter(Boolean);
    return parts.map((p) => {
      const idx = p.indexOf('|');
      if (idx >= 0) {
        return { name: p.slice(0, idx).trim(), phone: p.slice(idx + 1).trim() };
      }
      return { name: '', phone: p };
    }).filter(r => r.phone);
  };

  const load = useCallback(async () => {
    try {
      const c = await axios.get(`${API}/admin/pdc-reminder-config`, { headers: authHeader() });
      setCfg(c.data);
      setEmailInput((c.data.email_recipients || []).join(', '));
      setWaInput(formatWaRecipients(c.data.whatsapp_recipients));
      const l = await axios.get(`${API}/admin/pdc-reminder-log?limit=50`, { headers: authHeader() });
      setLogRows(l.data.entries || []);
    } catch (e) {
      // Panel is admin-only. Non-admins just see nothing.
      setCfg(null);
    }
  }, []);

  useEffect(() => { if (expanded && !cfg) load(); }, [expanded, cfg, load]);

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        email_recipients: emailInput.split(/[,\n]/).map(s => s.trim()).filter(Boolean),
        whatsapp_recipients: parseWaRecipients(waInput),
        enabled: cfg?.enabled !== false,
      };
      await axios.post(`${API}/admin/pdc-reminder-config`, payload, { headers: authHeader() });
      toast.success('Recipients saved');
      await load();
    } catch (e) {
      toast.error('Failed to save recipients');
    }
    setBusy(false);
  };

  const runSweep = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/pdc-reminder-run`, {}, { headers: authHeader() });
      const d = r.data || {};
      toast.success(`Sweep done: sent ${d.sent_count || 0}, skipped ${d.skipped_count || 0}`);
      await load();
    } catch (e) {
      toast.error('Sweep failed');
    }
    setBusy(false);
  };

  const toggleEnabled = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/pdc-reminder-config`, {
        email_recipients: cfg?.email_recipients || [],
        whatsapp_recipients: cfg?.whatsapp_recipients || [],
        enabled: !(cfg?.enabled !== false),
      }, { headers: authHeader() });
      await load();
    } catch (e) { /* noop */ }
    setBusy(false);
  };

  return (
    <Card data-testid="pdc-reminder-panel">
      <CardContent className="p-4">
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full flex items-center justify-between text-left"
          data-testid="pdc-reminder-toggle"
        >
          <div>
            <h3 className="text-sm font-semibold text-slate-800">📮 PDC Cheque Reminders</h3>
            <p className="text-xs text-slate-500">
              Auto-emails + WhatsApp at 7/5/2/1 days before each PDC cheque due date
            </p>
          </div>
          <span className="text-slate-400">{expanded ? '▲' : '▼'}</span>
        </button>

        {expanded && (
          <div className="mt-4 space-y-4">
            {!cfg ? (
              <p className="text-xs text-slate-400">Loading… (admin access required)</p>
            ) : (
              <>
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant={cfg.enabled !== false ? 'default' : 'outline'}>
                    {cfg.enabled !== false ? 'Active' : 'Disabled'}
                  </Badge>
                  <Button size="sm" variant="outline" onClick={toggleEnabled} disabled={busy}
                    data-testid="pdc-reminder-toggle-enabled">
                    {cfg.enabled !== false ? 'Disable' : 'Enable'}
                  </Button>
                  <Button size="sm" onClick={runSweep} disabled={busy}
                    data-testid="pdc-reminder-run-now">
                    Run Sweep Now
                  </Button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Email recipients (comma-separated)</Label>
                    <Textarea rows={2} value={emailInput}
                      onChange={(e) => setEmailInput(e.target.value)}
                      data-testid="pdc-reminder-emails" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">WhatsApp recipients — one per line, format: <code className="text-[10px] bg-slate-100 px-1 rounded">Name | Phone</code></Label>
                    <Textarea rows={4} value={waInput}
                      onChange={(e) => setWaInput(e.target.value)}
                      placeholder={"Mahesh (Accounts) | +971504513470\nKaustubh (Owner) | +971557991499\nRaj (Landlord) | +919762986597"}
                      data-testid="pdc-reminder-whatsapps" />
                    <p className="text-[10px] text-slate-500">
                      Names are used to auto-tag the number in the WhatsApp Contacts list. Bare phone numbers without a name still work but will show as &ldquo;PDC Reminder Recipient&rdquo;.
                    </p>
                  </div>
                </div>
                <Button size="sm" onClick={save} disabled={busy} data-testid="pdc-reminder-save">
                  Save Recipients
                </Button>

                <div>
                  <h4 className="text-xs font-semibold text-slate-700 mb-1">Recent Reminders Sent</h4>
                  {logRows.length === 0 ? (
                    <p className="text-xs text-slate-400">No reminders sent yet.</p>
                  ) : (
                    <div className="overflow-x-auto border rounded-md max-h-72">
                      <table className="min-w-full text-xs">
                        <thead className="bg-slate-50 sticky top-0">
                          <tr>
                            <th className="text-left px-2 py-1">Sent At</th>
                            <th className="text-left px-2 py-1">Cheque</th>
                            <th className="text-left px-2 py-1">Days Out</th>
                            <th className="text-left px-2 py-1">Email Delivery</th>
                            <th className="text-left px-2 py-1">WhatsApp Delivery</th>
                          </tr>
                        </thead>
                        <tbody>
                          {logRows.map((r) => (
                            <tr key={r.id} className="border-t">
                              <td className="px-2 py-1 whitespace-nowrap">
                                {r.sent_at ? new Date(r.sent_at).toLocaleString() : '—'}
                              </td>
                              <td className="px-2 py-1 font-mono">{r.cheque_id?.slice(0, 8)}</td>
                              <td className="px-2 py-1">{r.days_out} day(s)</td>
                              <td className="px-2 py-1">
                                {Object.entries(r.delivery?.email || {}).map(([addr, st]) => (
                                  <div key={addr} className="text-[10px]">
                                    <span className="text-slate-500">{addr}:</span>{' '}
                                    <span className={st === 'sent' ? 'text-emerald-600' : 'text-rose-600'}>
                                      {st}
                                    </span>
                                  </div>
                                ))}
                              </td>
                              <td className="px-2 py-1">
                                {Object.entries(r.delivery?.whatsapp || {}).map(([num, st]) => (
                                  <div key={num} className="text-[10px]">
                                    <span className="text-slate-500">{num}:</span>{' '}
                                    <span className={st === 'sent' ? 'text-emerald-600' : 'text-amber-600'}>
                                      {st}
                                    </span>
                                  </div>
                                ))}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};


const RentalExpenses = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [swapLocations, setSwapLocations] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [detailProp, setDetailProp] = useState(null);
  const [paymentDialog, setPaymentDialog] = useState({ open: false });

  const getAuth = () => {
    const token = localStorage.getItem('token');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  const loadEntries = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/accounts/rental-expenses`, getAuth());
      setEntries(r.data.entries || []);
    } catch (e) {
      toast.error(formatErrorMessage(e, 'Failed to load rental expenses'));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadSwapLocations = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/swap-locations`, getAuth());
      setSwapLocations(r.data || []);
    } catch {
      // silently ignore — dropdown just stays empty
    }
  }, []);

  useEffect(() => { loadEntries(); loadSwapLocations(); }, [loadEntries, loadSwapLocations]);

  // ---------- Form helpers ----------
  const resetForm = () => { setForm(emptyForm); setEditingId(null); };

  const openCreateDialog = () => { resetForm(); setDialogOpen(true); };
  const openEditDialog = (p) => {
    setForm({
      property_type: p.property_type,
      property_name: p.property_name,
      swap_location_id: p.swap_location_id || '',
      real_estator: p.real_estator || '',
      real_estator_contact: p.real_estator_contact || { name: '', email: '', phone: '' },
      property_owner: p.property_owner || '',
      tenancy_contract_no: p.tenancy_contract_no || '',
      dewa_premises_no: p.dewa_premises_no || '',
      tenancy_start_date: p.tenancy_start_date || '',
      tenancy_expiry_date: p.tenancy_expiry_date || '',
      tenancy_contract_urls: (p.tenancy_contract_urls || []).map(f => ({ ...f })),
      contract_value: p.contract_value || 0,
      security_deposit: p.security_deposit || 0,
      dewa_deposit: p.dewa_deposit || 0,
      shared_pct_electricity: p.shared_pct_electricity ?? '',
      shared_pct_rent: p.shared_pct_rent ?? '',
      other_deposits: (p.other_deposits || []).map(d => ({ ...d })),
      other_fees: (p.other_fees || []).map(d => ({ ...d })),
      initial_payments: [],  // starts empty on Edit; user may add new payments which will be appended alongside existing ledger entries
    });
    setEditingId(p.id);
    setDialogOpen(true);
  };

  const updateForm = (patch) => setForm((f) => ({ ...f, ...patch }));

  const addLine = (key) => setForm((f) => ({ ...f, [key]: [...(f[key] || []), { description: '', amount: 0 }] }));
  const removeLine = (key, idx) => setForm((f) => ({ ...f, [key]: f[key].filter((_, i) => i !== idx) }));
  const updateLine = (key, idx, patch) => setForm((f) => ({
    ...f, [key]: f[key].map((row, i) => (i === idx ? { ...row, ...patch } : row)),
  }));

  const submitForm = async () => {
    if (!form.property_name.trim()) { toast.error('Property Name is required'); return; }
    if (form.property_type === 'charging_point' && !form.swap_location_id) {
      toast.error('Please select a Battery Swap Location'); return;
    }
    // Validate any initial payments the user is adding along with the property.
    for (let i = 0; i < (form.initial_payments || []).length; i++) {
      const p = form.initial_payments[i];
      if (p.mode === 'cheque') {
        if (!p.cheques || p.cheques.length === 0) {
          toast.error(`Payment #${i + 1}: add at least one cheque`); return;
        }
        for (const c of p.cheques) {
          if (!c.cheque_no?.trim()) { toast.error(`Payment #${i + 1}: every cheque needs a cheque no.`); return; }
          if (!(Number(c.amount) > 0)) { toast.error(`Payment #${i + 1}: every cheque needs an amount`); return; }
        }
      } else if (p.mode === 'bank_transfer') {
        if (!(Number(p.bank_transfer?.amount) > 0)) {
          toast.error(`Payment #${i + 1}: bank transfer amount is required`); return;
        }
      }
    }
    try {
      const { initial_payments = [], ...propertyPayload } = form;
      // For charging_point, sync property_name to the chosen swap location's label.
      if (form.property_type === 'charging_point') {
        const sl = swapLocations.find((s) => s.id === form.swap_location_id);
        if (sl) propertyPayload.property_name = sl.name;
      }
      // Convert blank Analysis-override inputs to null so backend treats
      // them as "inherit global slider" instead of "0%".
      propertyPayload.shared_pct_electricity =
        propertyPayload.shared_pct_electricity === '' || propertyPayload.shared_pct_electricity === null
          ? null : parseFloat(propertyPayload.shared_pct_electricity);
      propertyPayload.shared_pct_rent =
        propertyPayload.shared_pct_rent === '' || propertyPayload.shared_pct_rent === null
          ? null : parseFloat(propertyPayload.shared_pct_rent);
      let savedId = editingId;
      if (editingId) {
        await axios.put(`${API}/accounts/rental-expenses/${editingId}`, propertyPayload, getAuth());
      } else {
        const r = await axios.post(`${API}/accounts/rental-expenses`, propertyPayload, getAuth());
        savedId = r.data.id;
      }
      // Attach each initial payment — works for BOTH create and edit
      // flows. On edit the new payments are appended alongside the
      // existing ledger; the Detail dialog still lets ops delete
      // legacy entries.
      if (savedId && initial_payments.length > 0) {
        for (const p of initial_payments) {
          // Strip the client-only `_key` before sending.
          const { _key, ...body } = p;
          if (p.mode === 'bank_transfer' && !p.bank_transfer) body.bank_transfer = null;
          await axios.post(
            `${API}/accounts/rental-expenses/${savedId}/payments`,
            body,
            getAuth(),
          );
        }
      }
      toast.success(
        editingId
          ? `Property updated${initial_payments.length ? ` with ${initial_payments.length} new payment(s)` : ''}`
          : `Property added${initial_payments.length ? ` with ${initial_payments.length} payment(s)` : ''}`
      );
      setDialogOpen(false);
      resetForm();
      await loadEntries();
    } catch (e) {
      toast.error(formatErrorMessage(e, 'Save failed'));
    }
  };

  const deleteProperty = async (id) => {
    if (!window.confirm('Delete this property and all its payments?')) return;
    try {
      await axios.delete(`${API}/accounts/rental-expenses/${id}`, getAuth());
      toast.success('Deleted');
      await loadEntries();
    } catch (e) {
      toast.error(formatErrorMessage(e, 'Delete failed'));
    }
  };

  // ---------- Detail view ----------
  const openDetail = async (p) => {
    try {
      const r = await axios.get(`${API}/accounts/rental-expenses/${p.id}`, getAuth());
      setDetailProp(r.data);
    } catch (e) {
      toast.error(formatErrorMessage(e, 'Failed to load property'));
    }
  };

  const openViewChequesModal = (p) => {
    openDetail(p);
  };

  return (
    <div className="space-y-4" data-testid="rental-expenses">
      <PdcReminderPanel />
      <Card>
        <CardContent className="p-4 sm:p-6 space-y-4">
          {/* Header */}
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <h2 className="text-lg font-semibold text-slate-800">Property Rental Expenses</h2>
              <p className="text-xs text-slate-500">
                Charging point & accommodation rentals — tenancy, deposits and cheque schedule
              </p>
            </div>
            <Button onClick={openCreateDialog} data-testid="rental-add-property">
              <Plus className="w-4 h-4 mr-1" /> Add Property
            </Button>
          </div>

          {/* Log table */}
          <div className="border rounded-md overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-xs">Property Name</TableHead>
                  <TableHead className="text-xs">Type</TableHead>
                  <TableHead className="text-xs">Tenancy Start</TableHead>
                  <TableHead className="text-xs">Tenancy Expiry</TableHead>
                  <TableHead className="text-xs text-right">Contract Value</TableHead>
                  <TableHead className="text-xs">PDC Cheques (due dates)</TableHead>
                  <TableHead className="text-xs">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                      <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…
                    </TableCell>
                  </TableRow>
                ) : entries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                      No rental properties yet. Click <span className="font-medium">Add Property</span> to get started.
                    </TableCell>
                  </TableRow>
                ) : entries.map((p) => (
                  <TableRow
                    key={p.id}
                    className="cursor-pointer hover:bg-slate-50"
                    data-testid={`rental-row-${p.id}`}
                    onClick={() => openDetail(p)}
                  >
                    <TableCell className="text-xs font-medium text-slate-800">
                      <div className="flex items-center gap-1.5">
                        {p.property_type === 'charging_point'
                          ? <Zap className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                          : <Home className="w-3.5 h-3.5 text-indigo-500 shrink-0" />}
                        <span>{p.property_name}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-xs">
                      <Badge variant="outline" className="text-[10px]">
                        {p.property_type === 'charging_point' ? 'Charging Point' : 'Accommodation'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs whitespace-nowrap">{p.tenancy_start_date || '-'}</TableCell>
                    <TableCell className="text-xs whitespace-nowrap">{p.tenancy_expiry_date || '-'}</TableCell>
                    <TableCell className="text-xs text-right font-semibold">{formatMoney(p.contract_value)}</TableCell>
                    <TableCell className="text-xs">
                      {p.pdc_cheques_count > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {p.pdc_cheques.slice(0, 4).map((c) => (
                            <span
                              key={c.id}
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-amber-50 border border-amber-200 rounded text-[10px] text-amber-900 whitespace-nowrap"
                              title={`Cheque #${c.cheque_no} — ${CATEGORY_LABEL[c.category] || c.category}`}
                            >
                              <CalendarDays className="w-2.5 h-2.5" /> {c.date || '—'}
                            </span>
                          ))}
                          {p.pdc_cheques.length > 4 && (
                            <span className="text-[10px] text-slate-500">
                              +{p.pdc_cheques.length - 4} more
                            </span>
                          )}
                        </div>
                      ) : <span className="text-slate-400">—</span>}
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openViewChequesModal(p)}
                          className="h-7 text-blue-600 text-xs hover:bg-blue-50"
                          data-testid={`rental-view-cheques-${p.id}`}
                          disabled={p.pdc_cheques_count === 0}
                          title="View cheques"
                        >
                          <Eye className="w-3.5 h-3.5 mr-1" /> Cheques
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(p)}
                          className="h-7 text-xs"
                          data-testid={`rental-edit-${p.id}`}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteProperty(p.id)}
                          className="h-7 text-red-600 hover:bg-red-50 text-xs"
                          data-testid={`rental-delete-${p.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Add / Edit dialog */}
      <PropertyFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        form={form}
        setForm={setForm}
        updateForm={updateForm}
        addLine={addLine}
        removeLine={removeLine}
        updateLine={updateLine}
        submit={submitForm}
        editing={!!editingId}
        editingId={editingId}
        swapLocations={swapLocations}
      />

      {/* Detail dialog */}
      {detailProp && (
        <PropertyDetailDialog
          property={detailProp}
          onOpenChange={(v) => !v && setDetailProp(null)}
          onAddPayment={() => setPaymentDialog({ open: true })}
          onDeletePayment={async (payId) => {
            if (!window.confirm('Delete this payment?')) return;
            try {
              const r = await axios.delete(
                `${API}/accounts/rental-expenses/${detailProp.id}/payments/${payId}`,
                getAuth(),
              );
              setDetailProp(r.data);
              toast.success('Payment removed');
              await loadEntries();
            } catch (e) {
              toast.error(formatErrorMessage(e, 'Delete failed'));
            }
          }}
        />
      )}

      {/* Add Payment dialog */}
      {detailProp && paymentDialog.open && (
        <PaymentFormDialog
          property={detailProp}
          onClose={() => setPaymentDialog({ open: false })}
          onSaved={async (updated) => {
            setDetailProp(updated);
            setPaymentDialog({ open: false });
            await loadEntries();
          }}
        />
      )}
    </div>
  );
};

/* ============================================================
   Add / Edit Property form dialog
   ============================================================ */
const PropertyFormDialog = ({
  open, onOpenChange, form, setForm, updateForm,
  addLine, removeLine, updateLine, submit, editing, editingId, swapLocations,
}) => {
  // Payments captured inline during property creation.
  const addPayment = () => setForm((f) => ({
    ...f,
    initial_payments: [...(f.initial_payments || []), {
      _key: Math.random().toString(36).slice(2),
      category: 'contract_value',
      mode: 'cheque',
      cheques: [{ cheque_no: '', cheque_type: 'PDC', date: '', amount: 0, copy_url: '' }],
      bank_transfer: null,
    }],
  }));
  const removePayment = (idx) => setForm((f) => ({
    ...f, initial_payments: (f.initial_payments || []).filter((_, i) => i !== idx),
  }));
  const patchPayment = (idx, patch) => setForm((f) => ({
    ...f,
    initial_payments: (f.initial_payments || []).map((p, i) => (i === idx ? { ...p, ...patch } : p)),
  }));
  const addChequeToPayment = (idx) => setForm((f) => ({
    ...f,
    initial_payments: f.initial_payments.map((p, i) => (i === idx
      ? { ...p, cheques: [...(p.cheques || []), { cheque_no: '', cheque_type: 'PDC', date: '', amount: 0, copy_url: '' }] }
      : p)),
  }));
  const removeChequeFromPayment = (idx, cIdx) => setForm((f) => ({
    ...f,
    initial_payments: f.initial_payments.map((p, i) => (i === idx
      ? { ...p, cheques: p.cheques.filter((_, ci) => ci !== cIdx) }
      : p)),
  }));
  const patchChequeInPayment = (idx, cIdx, patch) => setForm((f) => ({
    ...f,
    initial_payments: f.initial_payments.map((p, i) => (i === idx
      ? { ...p, cheques: p.cheques.map((c, ci) => (ci === cIdx ? { ...c, ...patch } : c)) }
      : p)),
  }));

  // ---- Tenancy Contract Agreement multi-upload ----
  const TENANCY_MAX_MB = 10;
  const TENANCY_ACCEPT = '.pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png';
  const [tenancyUploading, setTenancyUploading] = useState(false);
  const handleTenancyUpload = async (files) => {
    const list = Array.from(files || []);
    if (list.length === 0) return;
    setTenancyUploading(true);
    try {
      const token = localStorage.getItem('token');
      const uploaded = [];
      for (const f of list) {
        const okType = /^(application\/pdf|image\/(png|jpe?g))$/i.test(f.type) ||
          /\.(pdf|png|jpe?g)$/i.test(f.name);
        if (!okType) { toast.error(`${f.name}: only PDF, JPG or PNG allowed`); continue; }
        if (f.size > TENANCY_MAX_MB * 1024 * 1024) {
          toast.error(`${f.name}: exceeds ${TENANCY_MAX_MB} MB limit`); continue;
        }
        const fd = new FormData();
        fd.append('file', f);
        const r = await axios.post(
          `${API}/accounts/rental-expenses/tenancy-contract/upload`,
          fd,
          { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } },
        );
        uploaded.push(r.data);
      }
      if (uploaded.length) {
        setForm((f) => ({ ...f, tenancy_contract_urls: [...(f.tenancy_contract_urls || []), ...uploaded] }));
        toast.success(`Uploaded ${uploaded.length} file(s)`);
      }
    } catch (e) {
      toast.error(formatErrorMessage(e, 'Upload failed'));
    } finally {
      setTenancyUploading(false);
    }
  };
  const removeTenancyFile = (idx) => setForm((f) => ({
    ...f, tenancy_contract_urls: f.tenancy_contract_urls.filter((_, i) => i !== idx),
  }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editing ? 'Edit Rental Property' : 'Add Rental Property'}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Type + Name */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Type *</Label>
              <Select
                value={form.property_type}
                onValueChange={(v) => updateForm({
                  property_type: v,
                  property_name: v === 'charging_point' ? '' : form.property_name,
                  swap_location_id: v === 'charging_point' ? form.swap_location_id : '',
                })}
              >
                <SelectTrigger data-testid="rental-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="charging_point"><Zap className="w-3 h-3 inline mr-1 text-amber-500" /> Charging Point</SelectItem>
                  <SelectItem value="accommodation"><Home className="w-3 h-3 inline mr-1 text-indigo-500" /> Accommodation</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Property Name *</Label>
              {form.property_type === 'charging_point' ? (
                <Select
                  value={form.swap_location_id || ''}
                  onValueChange={(v) => {
                    const loc = swapLocations.find((s) => s.id === v);
                    updateForm({ swap_location_id: v, property_name: loc?.name || '' });
                  }}
                >
                  <SelectTrigger data-testid="rental-swap-location">
                    <SelectValue placeholder="Select Battery Swap Location" />
                  </SelectTrigger>
                  <SelectContent>
                    {swapLocations.length === 0 && (
                      <div className="text-xs text-slate-400 px-2 py-1.5">No swap locations found</div>
                    )}
                    {swapLocations.map((s) => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  value={form.property_name}
                  onChange={(e) => updateForm({ property_name: e.target.value })}
                  placeholder="e.g., Al Nahda Rider Villa 2"
                  data-testid="rental-property-name"
                />
              )}
            </div>
          </div>

          {/* Real Estator + Property Owner */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Real Estator</Label>
              <Textarea
                value={form.real_estator}
                onChange={(e) => updateForm({ real_estator: e.target.value })}
                placeholder="Firm / agency name and reference"
                rows={2}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Property Owner</Label>
              <Textarea
                value={form.property_owner}
                onChange={(e) => updateForm({ property_owner: e.target.value })}
                placeholder="Owner name"
                rows={2}
              />
            </div>
          </div>

          {/* Real Estator contact */}
          <div className="border rounded-md p-3 space-y-3 bg-slate-50/50">
            <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Real Estator Contact Details</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Name</Label>
                <Input
                  value={form.real_estator_contact?.name || ''}
                  onChange={(e) => updateForm({ real_estator_contact: { ...form.real_estator_contact, name: e.target.value } })}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Email</Label>
                <Input
                  type="email"
                  value={form.real_estator_contact?.email || ''}
                  onChange={(e) => updateForm({ real_estator_contact: { ...form.real_estator_contact, email: e.target.value } })}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Contact No.</Label>
                <Input
                  value={form.real_estator_contact?.phone || ''}
                  onChange={(e) => updateForm({ real_estator_contact: { ...form.real_estator_contact, phone: e.target.value } })}
                  placeholder="+971 5x xxx xxxx"
                />
              </div>
            </div>
          </div>

          {/* Tenancy details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Tenancy Contract No.</Label>
              <Input
                value={form.tenancy_contract_no}
                maxLength={25}
                onChange={(e) => updateForm({ tenancy_contract_no: e.target.value.toUpperCase() })}
                placeholder="Up to 25 characters"
              />
            </div>
            <div className="space-y-1.5">
              <Label>DEWA Premises No.</Label>
              <Input
                value={form.dewa_premises_no}
                maxLength={10}
                onChange={(e) => updateForm({ dewa_premises_no: e.target.value.toUpperCase() })}
                placeholder="Up to 10 characters"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Tenancy Start Date</Label>
              <Input
                type="date"
                value={form.tenancy_start_date}
                onChange={(e) => updateForm({ tenancy_start_date: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Tenancy Expiry Date</Label>
              <Input
                type="date"
                value={form.tenancy_expiry_date}
                onChange={(e) => updateForm({ tenancy_expiry_date: e.target.value })}
              />
            </div>
          </div>

          {/* Tenancy Contract Agreement — multi-file upload
              PDF / JPEG / PNG, up to 10 MB per file. */}
          <div className="rounded-md border border-slate-200 bg-white p-3 space-y-2">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div>
                <Label>Tenancy Contract Agreement</Label>
                <p className="text-[11px] text-slate-500 mt-0.5">
                  Upload signed agreement copies · PDF, JPEG or PNG · up to 10 MB each · multiple files supported
                </p>
              </div>
              <label className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border border-slate-300 bg-slate-50 hover:bg-slate-100 cursor-pointer">
                <Upload className="w-3.5 h-3.5" />
                {tenancyUploading ? 'Uploading…' : 'Choose files'}
                <input
                  type="file"
                  multiple
                  accept={TENANCY_ACCEPT}
                  className="hidden"
                  disabled={tenancyUploading}
                  onChange={(e) => { handleTenancyUpload(e.target.files); e.target.value = ''; }}
                  data-testid="tenancy-contract-file-input"
                />
              </label>
            </div>
            {(form.tenancy_contract_urls || []).length > 0 && (
              <div className="space-y-1" data-testid="tenancy-contract-file-list">
                {form.tenancy_contract_urls.map((f, idx) => (
                  <div
                    key={f.url || idx}
                    className="flex items-center justify-between gap-2 rounded border border-slate-200 bg-slate-50 px-2 py-1.5 text-xs"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <FileText className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                      <a
                        href={f.url}
                        target="_blank"
                        rel="noreferrer"
                        className="truncate text-blue-600 hover:underline"
                        title={f.filename || 'Tenancy contract'}
                      >
                        {f.filename || `Contract ${idx + 1}`}
                      </a>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeTenancyFile(idx)}
                      className="text-slate-400 hover:text-rose-600 shrink-0"
                      title="Remove"
                      data-testid={`tenancy-contract-remove-${idx}`}
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Contract value + deposits */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-1.5">
              <Label>Contract Value (AED)</Label>
              <Input type="number" value={form.contract_value} onChange={(e) => updateForm({ contract_value: parseFloat(e.target.value) || 0 })} />
            </div>
            <div className="space-y-1.5">
              <Label>Security Deposit (AED)</Label>
              <Input type="number" value={form.security_deposit} onChange={(e) => updateForm({ security_deposit: parseFloat(e.target.value) || 0 })} />
            </div>
            <div className="space-y-1.5">
              <Label>DEWA Deposit (AED)</Label>
              <Input type="number" value={form.dewa_deposit} onChange={(e) => updateForm({ dewa_deposit: parseFloat(e.target.value) || 0 })} />
            </div>
          </div>

          {/* Analysis Dashboard — per-property shared-premises overrides.
              Leave blank to inherit the global sliders on the Analysis
              → Cost & Energy tab. Values are 0-100 %. */}
          <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
            <div className="text-xs font-medium text-slate-700 mb-2">
              Analysis dashboard — shared-premises attribution overrides
              <span className="text-slate-500 font-normal"> (leave blank to inherit global defaults)</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs">Electricity attributable to battery swaps (%)</Label>
                <Input
                  type="number" min="0" max="100" step="1"
                  placeholder="Inherit global"
                  value={form.shared_pct_electricity}
                  onChange={(e) => updateForm({ shared_pct_electricity: e.target.value })}
                  data-testid="rental-shared-pct-electricity"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Rent attributable to battery swaps (%)</Label>
                <Input
                  type="number" min="0" max="100" step="1"
                  placeholder="Inherit global"
                  value={form.shared_pct_rent}
                  onChange={(e) => updateForm({ shared_pct_rent: e.target.value })}
                  data-testid="rental-shared-pct-rent"
                />
              </div>
            </div>
          </div>

          {/* Other deposits */}
          <RepeatingLines
            title="Other Deposits"
            lines={form.other_deposits}
            onAdd={() => addLine('other_deposits')}
            onRemove={(i) => removeLine('other_deposits', i)}
            onChange={(i, patch) => updateLine('other_deposits', i, patch)}
          />

          {/* Other fees */}
          <RepeatingLines
            title="Other Fees"
            lines={form.other_fees}
            onAdd={() => addLine('other_fees')}
            onRemove={(i) => removeLine('other_fees', i)}
            onChange={(i, patch) => updateLine('other_fees', i, patch)}
          />

          {/* Payments — available in both Add and Edit flows. On Edit
              the new payments are APPENDED to the existing ledger; the
              Detail dialog still lists / deletes historical payments. */}
          <div className="border rounded-md p-3 bg-emerald-50/40 border-emerald-200">
            <div className="flex items-center justify-between mb-2">
              <div>
                <p className="text-xs font-semibold text-emerald-800 uppercase tracking-wide">
                  {editing ? 'Add New Payments (optional)' : 'Payments (optional)'}
                </p>
                <p className="text-[10px] text-slate-500">
                  {editing
                    ? 'Add cheques or bank transfers here — they will be appended to the existing ledger when you save.'
                    : 'Add cheques or bank transfers now, or later from the property detail view.'}
                </p>
              </div>
              <Button size="sm" variant="outline" type="button" onClick={addPayment} data-testid="rental-form-add-payment">
                <Plus className="w-3 h-3 mr-1" /> Add Payment
              </Button>
            </div>

            {(form.initial_payments || []).length === 0 ? (
              <p className="text-xs text-slate-400 italic">No payments added yet</p>
            ) : (
              <div className="space-y-3">
                {form.initial_payments.map((p, idx) => (
                  <PaymentBlock
                    key={p._key}
                    idx={idx}
                    payment={p}
                    propId={editingId || 'new'}
                    onPatch={(patch) => patchPayment(idx, patch)}
                    onRemove={() => removePayment(idx)}
                    onAddCheque={() => addChequeToPayment(idx)}
                    onRemoveCheque={(cIdx) => removeChequeFromPayment(idx, cIdx)}
                    onPatchCheque={(cIdx, patch) => patchChequeInPayment(idx, cIdx, patch)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} data-testid="rental-submit">
            {editing ? 'Save Changes' : 'Add Property'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const RepeatingLines = ({ title, lines, onAdd, onRemove, onChange }) => (
  <div className="border rounded-md p-3 bg-slate-50/50">
    <div className="flex items-center justify-between mb-2">
      <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">{title} (optional)</p>
      <Button size="sm" variant="outline" type="button" onClick={onAdd}>
        <Plus className="w-3 h-3 mr-1" /> Add
      </Button>
    </div>
    {(lines || []).length === 0 ? (
      <p className="text-xs text-slate-400 italic">None added</p>
    ) : (
      <div className="space-y-2">
        {lines.map((line, i) => (
          <div key={i} className="grid grid-cols-12 gap-2 items-end">
            <div className="col-span-8">
              <Label className="text-xs">Description</Label>
              <Input
                value={line.description}
                onChange={(e) => onChange(i, { description: e.target.value })}
                placeholder="Reason / description"
              />
            </div>
            <div className="col-span-3">
              <Label className="text-xs">Amount (AED)</Label>
              <Input
                type="number"
                value={line.amount}
                onChange={(e) => onChange(i, { amount: parseFloat(e.target.value) || 0 })}
              />
            </div>
            <div className="col-span-1">
              <Button size="sm" variant="ghost" type="button" onClick={() => onRemove(i)} className="text-red-600 h-9">
                <X className="w-3 h-3" />
              </Button>
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
);

/* Inline payment block used inside the Add Property form. */
const PaymentBlock = ({ idx, payment, propId, onPatch, onRemove, onAddCheque, onRemoveCheque, onPatchCheque }) => {
  const [uploading, setUploading] = useState({});

  const getAuth = () => {
    const token = localStorage.getItem('token');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  const uploadFile = async (file, target) => {
    setUploading((u) => ({ ...u, [target]: true }));
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(
        `${API}/accounts/rental-expenses/${propId}/upload`,
        fd,
        { ...getAuth(), headers: { ...getAuth().headers, 'Content-Type': 'multipart/form-data' } },
      );
      return r.data.url;
    } finally {
      setUploading((u) => ({ ...u, [target]: false }));
    }
  };

  return (
    <div className="border rounded-md p-3 bg-white space-y-3" data-testid={`payment-block-${idx}`}>
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold text-slate-700">Payment #{idx + 1}</p>
        <Button size="sm" variant="ghost" type="button" onClick={onRemove} className="text-red-600 h-7">
          <X className="w-3 h-3" />
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="text-xs">Payment For *</Label>
          <Select value={payment.category} onValueChange={(v) => onPatch({ category: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {CATEGORY_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Payment Mode *</Label>
          <Select value={payment.mode} onValueChange={(v) => onPatch({
            mode: v,
            cheques: v === 'cheque'
              ? (payment.cheques && payment.cheques.length ? payment.cheques : [{ cheque_no: '', cheque_type: 'PDC', date: '', amount: 0, copy_url: '' }])
              : [],
            bank_transfer: v === 'bank_transfer'
              ? (payment.bank_transfer || { amount: 0, date: '', receipt_url: '' })
              : null,
          })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="cheque">Cheque</SelectItem>
              <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {payment.mode === 'cheque' ? (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Cheques</p>
            <Button size="sm" variant="outline" type="button" onClick={onAddCheque}>
              <Plus className="w-3 h-3 mr-1" /> Add cheque
            </Button>
          </div>
          {(payment.cheques || []).map((c, cIdx) => (
            <div key={cIdx} className="border rounded p-2 bg-slate-50 space-y-2">
              <div className="grid grid-cols-12 gap-2">
                <div className="col-span-4">
                  <Label className="text-[10px]">Cheque No.</Label>
                  <Input value={c.cheque_no} onChange={(e) => onPatchCheque(cIdx, { cheque_no: e.target.value })} />
                </div>
                <div className="col-span-2">
                  <Label className="text-[10px]">Type</Label>
                  <Select value={c.cheque_type} onValueChange={(v) => onPatchCheque(cIdx, { cheque_type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="CDC">CDC</SelectItem>
                      <SelectItem value="PDC">PDC</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-3">
                  <Label className="text-[10px]">Date</Label>
                  <Input type="date" value={c.date} onChange={(e) => onPatchCheque(cIdx, { date: e.target.value })} />
                </div>
                <div className="col-span-3">
                  <Label className="text-[10px]">Amount (AED)</Label>
                  <Input type="number" value={c.amount} onChange={(e) => onPatchCheque(cIdx, { amount: parseFloat(e.target.value) || 0 })} />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <label className="cursor-pointer inline-flex items-center gap-1 text-xs text-blue-600 hover:underline">
                  <Upload className="w-3 h-3" />
                  {c.copy_url ? 'Replace cheque copy' : 'Upload cheque copy'}
                  <input
                    type="file" accept="image/*,.pdf" className="hidden"
                    onChange={async (e) => {
                      const file = e.target.files?.[0]; if (!file) return;
                      try {
                        const url = await uploadFile(file, `cheque-${cIdx}`);
                        onPatchCheque(cIdx, { copy_url: url });
                        toast.success('Uploaded');
                      } catch (err) {
                        toast.error(formatErrorMessage(err, 'Upload failed'));
                      }
                    }}
                  />
                </label>
                {uploading[`cheque-${cIdx}`] && <Loader2 className="w-3 h-3 animate-spin text-slate-400" />}
                {c.copy_url && <span className="text-[10px] text-emerald-600">✓ attached</span>}
                <div className="flex-1" />
                {payment.cheques.length > 1 && (
                  <Button size="sm" variant="ghost" type="button" onClick={() => onRemoveCheque(cIdx)} className="text-red-600 h-7">
                    <X className="w-3 h-3" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="border rounded p-2 bg-slate-50 space-y-2">
          <div className="grid grid-cols-12 gap-2">
            <div className="col-span-6">
              <Label className="text-[10px]">Amount (AED)</Label>
              <Input type="number" value={payment.bank_transfer?.amount || 0} onChange={(e) => onPatch({
                bank_transfer: { ...(payment.bank_transfer || {}), amount: parseFloat(e.target.value) || 0 },
              })} />
            </div>
            <div className="col-span-6">
              <Label className="text-[10px]">Date</Label>
              <Input type="date" value={payment.bank_transfer?.date || ''} onChange={(e) => onPatch({
                bank_transfer: { ...(payment.bank_transfer || {}), date: e.target.value },
              })} />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <label className="cursor-pointer inline-flex items-center gap-1 text-xs text-blue-600 hover:underline">
              <Upload className="w-3 h-3" />
              {payment.bank_transfer?.receipt_url ? 'Replace transfer receipt' : 'Upload transfer receipt'}
              <input
                type="file" accept="image/*,.pdf" className="hidden"
                onChange={async (e) => {
                  const file = e.target.files?.[0]; if (!file) return;
                  try {
                    const url = await uploadFile(file, 'bank');
                    onPatch({ bank_transfer: { ...(payment.bank_transfer || {}), receipt_url: url } });
                    toast.success('Uploaded');
                  } catch (err) {
                    toast.error(formatErrorMessage(err, 'Upload failed'));
                  }
                }}
              />
            </label>
            {uploading.bank && <Loader2 className="w-3 h-3 animate-spin text-slate-400" />}
            {payment.bank_transfer?.receipt_url && <span className="text-[10px] text-emerald-600">✓ attached</span>}
          </div>
        </div>
      )}
    </div>
  );
};

/* ============================================================
   Property Detail dialog — shows every payment & every cheque
   ============================================================ */
const PropertyDetailDialog = ({ property, onOpenChange, onAddPayment, onDeletePayment }) => {
  const [openImg, setOpenImg] = useState(null);
  return (
    <Dialog open={!!property} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {property.property_type === 'charging_point'
              ? <Zap className="w-4 h-4 text-amber-500" />
              : <Home className="w-4 h-4 text-indigo-500" />}
            {property.property_name}
            <Badge variant="outline" className="ml-2 text-[10px]">
              {property.property_type === 'charging_point' ? 'Charging Point' : 'Accommodation'}
            </Badge>
          </DialogTitle>
        </DialogHeader>

        {/* Overview grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 py-2 text-xs">
          <MetaField label="Tenancy Start" value={property.tenancy_start_date || '-'} />
          <MetaField label="Tenancy Expiry" value={property.tenancy_expiry_date || '-'} />
          <MetaField label="Contract Value" value={formatMoney(property.contract_value)} />
          <MetaField label="Contract Paid" value={formatMoney(property.contract_value_paid)} />
          <MetaField label="Contract Pending" value={formatMoney(property.contract_value_pending)} highlight />
          <MetaField label="Security Deposit" value={formatMoney(property.security_deposit)} />
          <MetaField label="DEWA Deposit" value={formatMoney(property.dewa_deposit)} />
          <MetaField label="All Paid" value={formatMoney(property.all_paid_total)} />
          <MetaField label="Tenancy Contract No." value={property.tenancy_contract_no || '-'} />
          <MetaField label="DEWA Premises No." value={property.dewa_premises_no || '-'} />
          <MetaField label="Real Estator" value={property.real_estator || '-'} />
          <MetaField label="Property Owner" value={property.property_owner || '-'} />
        </div>

        {/* Contact */}
        {(property.real_estator_contact?.name || property.real_estator_contact?.email || property.real_estator_contact?.phone) && (
          <div className="border rounded p-2 bg-slate-50 text-xs">
            <p className="font-semibold text-slate-600 mb-1">Real Estator Contact</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-1">
              <span>Name: {property.real_estator_contact?.name || '-'}</span>
              <span>Email: {property.real_estator_contact?.email || '-'}</span>
              <span>Phone: {property.real_estator_contact?.phone || '-'}</span>
            </div>
          </div>
        )}

        {/* Other lines */}
        {(property.other_deposits?.length > 0 || property.other_fees?.length > 0) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
            {property.other_deposits?.length > 0 && (
              <div className="border rounded p-2">
                <p className="font-semibold text-slate-600 mb-1">Other Deposits</p>
                {property.other_deposits.map((d, i) => (
                  <div key={i} className="flex justify-between border-b border-dashed last:border-b-0 py-0.5">
                    <span className="truncate">{d.description || '-'}</span>
                    <span className="font-medium">{formatMoney(d.amount)}</span>
                  </div>
                ))}
              </div>
            )}
            {property.other_fees?.length > 0 && (
              <div className="border rounded p-2">
                <p className="font-semibold text-slate-600 mb-1">Other Fees</p>
                {property.other_fees.map((d, i) => (
                  <div key={i} className="flex justify-between border-b border-dashed last:border-b-0 py-0.5">
                    <span className="truncate">{d.description || '-'}</span>
                    <span className="font-medium">{formatMoney(d.amount)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Payments ledger */}
        <div className="flex items-center justify-between pt-2">
          <h3 className="font-semibold text-slate-700 text-sm">Payments Ledger</h3>
          <Button size="sm" onClick={onAddPayment} data-testid="rental-add-payment">
            <Plus className="w-3 h-3 mr-1" /> Add Payment
          </Button>
        </div>

        <div className="border rounded-md overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="text-xs">Category</TableHead>
                <TableHead className="text-xs">Mode</TableHead>
                <TableHead className="text-xs">Cheques / Transfer details</TableHead>
                <TableHead className="text-xs text-right">Amount</TableHead>
                <TableHead className="text-xs"> </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(property.payments || []).length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-slate-500 py-5 text-xs">
                    No payments logged yet.
                  </TableCell>
                </TableRow>
              ) : property.payments.map((pay) => {
                const total = pay.mode === 'cheque'
                  ? (pay.cheques || []).reduce((s, c) => s + Number(c.amount || 0), 0)
                  : Number(pay.bank_transfer?.amount || 0);
                return (
                  <TableRow key={pay.id}>
                    <TableCell className="text-xs">
                      <Badge className="bg-slate-100 text-slate-700 text-[10px]">
                        {CATEGORY_LABEL[pay.category] || pay.category}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs">
                      <Badge className={pay.mode === 'cheque'
                        ? 'bg-blue-100 text-blue-700 border-blue-200 text-[10px]'
                        : 'bg-emerald-100 text-emerald-700 border-emerald-200 text-[10px]'}>
                        {pay.mode === 'cheque' ? 'Cheque' : 'Bank Transfer'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs">
                      {pay.mode === 'cheque' ? (
                        <div className="space-y-1">
                          {(pay.cheques || []).map((c) => (
                            <div key={c.id} className="flex items-center gap-2 flex-wrap">
                              <Badge variant="outline" className="text-[10px] font-mono">#{c.cheque_no || '—'}</Badge>
                              <Badge className={c.cheque_type === 'PDC'
                                ? 'bg-amber-100 text-amber-800 text-[10px]'
                                : 'bg-lime-100 text-lime-800 text-[10px]'}>
                                {c.cheque_type}
                              </Badge>
                              <span className="text-slate-500">{c.date || '-'}</span>
                              <span className="font-medium">{formatMoney(c.amount)}</span>
                              {c.copy_url && (
                                <button
                                  type="button"
                                  onClick={() => setOpenImg(c.copy_url)}
                                  className="text-blue-600 hover:underline inline-flex items-center gap-0.5"
                                >
                                  <FileText className="w-3 h-3" /> View
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-slate-500">{pay.bank_transfer?.date || '-'}</span>
                          <span className="font-medium">{formatMoney(pay.bank_transfer?.amount)}</span>
                          {pay.bank_transfer?.receipt_url && (
                            <button
                              type="button"
                              onClick={() => setOpenImg(pay.bank_transfer.receipt_url)}
                              className="text-blue-600 hover:underline inline-flex items-center gap-0.5"
                            >
                              <FileText className="w-3 h-3" /> Receipt
                            </button>
                          )}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-right font-semibold">{formatMoney(total)}</TableCell>
                    <TableCell>
                      <Button size="sm" variant="ghost" onClick={() => onDeletePayment(pay.id)} className="text-red-600 h-7">
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>

      {/* File preview */}
      {openImg && (
        <Dialog open={!!openImg} onOpenChange={(v) => !v && setOpenImg(null)}>
          <DialogContent className="max-w-3xl">
            <DialogHeader><DialogTitle>File preview</DialogTitle></DialogHeader>
            {/\.(pdf)$/i.test(openImg) ? (
              <iframe title="doc" src={`${process.env.REACT_APP_BACKEND_URL}${openImg}`} className="w-full h-[70vh] border-0" />
            ) : (
              <img alt="preview" src={`${process.env.REACT_APP_BACKEND_URL}${openImg}`} className="max-h-[70vh] mx-auto" />
            )}
          </DialogContent>
        </Dialog>
      )}
    </Dialog>
  );
};

const MetaField = ({ label, value, highlight }) => (
  <div className={`border rounded p-2 ${highlight ? 'bg-amber-50 border-amber-200' : 'bg-white'}`}>
    <p className="text-[10px] uppercase tracking-wide text-slate-500">{label}</p>
    <p className="text-xs font-medium text-slate-800 mt-0.5 truncate" title={String(value)}>{value}</p>
  </div>
);

/* ============================================================
   Add Payment dialog
   ============================================================ */
const PaymentFormDialog = ({ property, onClose, onSaved }) => {
  const [category, setCategory] = useState('contract_value');
  const [mode, setMode] = useState('cheque');
  const [cheques, setCheques] = useState([
    { cheque_no: '', cheque_type: 'PDC', date: '', amount: 0, copy_url: '' },
  ]);
  const [bank, setBank] = useState({ amount: 0, date: '', receipt_url: '' });
  const [uploading, setUploading] = useState({});
  const [saving, setSaving] = useState(false);

  const getAuth = () => {
    const token = localStorage.getItem('token');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  const uploadFile = async (file, target) => {
    setUploading((u) => ({ ...u, [target]: true }));
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(
        `${API}/accounts/rental-expenses/${property.id}/upload`,
        fd,
        { ...getAuth(), headers: { ...getAuth().headers, 'Content-Type': 'multipart/form-data' } },
      );
      return r.data.url;
    } finally {
      setUploading((u) => ({ ...u, [target]: false }));
    }
  };

  const addCheque = () => setCheques((cs) => [
    ...cs, { cheque_no: '', cheque_type: 'PDC', date: '', amount: 0, copy_url: '' },
  ]);
  const removeCheque = (i) => setCheques((cs) => cs.filter((_, idx) => idx !== i));
  const updateCheque = (i, patch) => setCheques((cs) => cs.map((c, idx) => (idx === i ? { ...c, ...patch } : c)));

  // Contract Value mandates cheque | bank_transfer. Other categories:
  // allow either. Force mode==cheque if user pick contract_value on first render.
  const isContract = category === 'contract_value';

  const submit = async () => {
    // Validate
    if (mode === 'cheque') {
      if (cheques.length === 0) return toast.error('Add at least one cheque');
      for (const c of cheques) {
        if (!c.cheque_no?.trim()) return toast.error('Every cheque needs a cheque no.');
        if (!(c.amount > 0)) return toast.error('Every cheque needs an amount');
      }
    } else {
      if (!(bank.amount > 0)) return toast.error('Bank transfer amount is required');
    }
    setSaving(true);
    try {
      const payload = {
        category,
        mode,
        cheques: mode === 'cheque' ? cheques : [],
        bank_transfer: mode === 'bank_transfer' ? bank : null,
      };
      const r = await axios.post(
        `${API}/accounts/rental-expenses/${property.id}/payments`,
        payload,
        getAuth(),
      );
      toast.success('Payment added');
      onSaved(r.data);
    } catch (e) {
      toast.error(formatErrorMessage(e, 'Save failed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Add Payment</DialogTitle></DialogHeader>

        <div className="space-y-4 py-1">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Payment Category *</Label>
              <Select value={category} onValueChange={(v) => {
                setCategory(v);
                if (v === 'contract_value' && mode !== 'cheque' && mode !== 'bank_transfer') {
                  setMode('cheque');
                }
              }}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORY_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Payment Mode *</Label>
              <Select value={mode} onValueChange={setMode}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cheque">Cheque</SelectItem>
                  <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {mode === 'cheque' ? (
            <div className="border rounded-md p-3 space-y-3 bg-slate-50/50">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
                  Cheques {isContract && '(multiple allowed for Contract Value)'}
                </p>
                <Button size="sm" variant="outline" onClick={addCheque}>
                  <Plus className="w-3 h-3 mr-1" /> Add cheque
                </Button>
              </div>
              {cheques.map((c, i) => (
                <div key={i} className="border rounded p-2 bg-white space-y-2">
                  <div className="grid grid-cols-12 gap-2">
                    <div className="col-span-4">
                      <Label className="text-xs">Cheque No.</Label>
                      <Input value={c.cheque_no} onChange={(e) => updateCheque(i, { cheque_no: e.target.value })} />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs">Type</Label>
                      <Select value={c.cheque_type} onValueChange={(v) => updateCheque(i, { cheque_type: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="CDC">CDC</SelectItem>
                          <SelectItem value="PDC">PDC</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-3">
                      <Label className="text-xs">Date</Label>
                      <Input type="date" value={c.date} onChange={(e) => updateCheque(i, { date: e.target.value })} />
                    </div>
                    <div className="col-span-3">
                      <Label className="text-xs">Amount (AED)</Label>
                      <Input type="number" value={c.amount} onChange={(e) => updateCheque(i, { amount: parseFloat(e.target.value) || 0 })} />
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="cursor-pointer inline-flex items-center gap-1 text-xs text-blue-600 hover:underline">
                      <Upload className="w-3 h-3" />
                      {c.copy_url ? 'Replace cheque copy' : 'Upload cheque copy'}
                      <input
                        type="file" accept="image/*,.pdf" className="hidden"
                        onChange={async (e) => {
                          const file = e.target.files?.[0]; if (!file) return;
                          try {
                            const url = await uploadFile(file, `cheque-${i}`);
                            updateCheque(i, { copy_url: url });
                            toast.success('Uploaded');
                          } catch (err) {
                            toast.error(formatErrorMessage(err, 'Upload failed'));
                          }
                        }}
                      />
                    </label>
                    {uploading[`cheque-${i}`] && <Loader2 className="w-3 h-3 animate-spin text-slate-400" />}
                    {c.copy_url && <span className="text-[10px] text-emerald-600">✓ file attached</span>}
                    <div className="flex-1" />
                    {cheques.length > 1 && (
                      <Button size="sm" variant="ghost" onClick={() => removeCheque(i)} className="text-red-600 h-7">
                        <X className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="border rounded-md p-3 space-y-3 bg-slate-50/50">
              <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Bank Transfer</p>
              <div className="grid grid-cols-12 gap-2">
                <div className="col-span-6">
                  <Label className="text-xs">Amount (AED)</Label>
                  <Input type="number" value={bank.amount} onChange={(e) => setBank({ ...bank, amount: parseFloat(e.target.value) || 0 })} />
                </div>
                <div className="col-span-6">
                  <Label className="text-xs">Date</Label>
                  <Input type="date" value={bank.date} onChange={(e) => setBank({ ...bank, date: e.target.value })} />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <label className="cursor-pointer inline-flex items-center gap-1 text-xs text-blue-600 hover:underline">
                  <Upload className="w-3 h-3" />
                  {bank.receipt_url ? 'Replace transfer receipt' : 'Upload transfer receipt'}
                  <input
                    type="file" accept="image/*,.pdf" className="hidden"
                    onChange={async (e) => {
                      const file = e.target.files?.[0]; if (!file) return;
                      try {
                        const url = await uploadFile(file, 'bank');
                        setBank({ ...bank, receipt_url: url });
                        toast.success('Uploaded');
                      } catch (err) {
                        toast.error(formatErrorMessage(err, 'Upload failed'));
                      }
                    }}
                  />
                </label>
                {uploading.bank && <Loader2 className="w-3 h-3 animate-spin text-slate-400" />}
                {bank.receipt_url && <span className="text-[10px] text-emerald-600">✓ file attached</span>}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="rental-payment-save">
            {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
            Save Payment
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RentalExpenses;
